import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TIPI = [["appuntamento", "Appuntamento"], ["richiamo", "Richiamo"], ["compito", "Compito"], ["manuale", "Manuale"]];

const initial = (practice, client, userId) => {
  const name = (client.ragione_sociale || `${client.nome || ""} ${client.cognome || ""}`).trim();
  return { titolo: name ? `Appuntamento - ${name}` : "Appuntamento", tipo: "appuntamento", start: "", duration_min: 60, reminder_hours: 0,
    sede_id: practice.sede_id || "none", assigned_to: practice.assigned_to || userId || "none", cliente_nome: name,
    cellulare: client.telefono || "", email: client.email || "", note: "" };
};

export const PracticeEventDialog = ({ practice, client, userId, sedi, onClose, onCreated }) => {
  const [f, setF] = useState(() => initial(practice, client, userId));
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/users/internal").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.titolo.trim() || !f.start || f.sede_id === "none") { toast.error("Titolo, data/ora e sede obbligatori"); return; }
    setBusy(true);
    try {
      await api.post("/calendar", { ...f, practice_id: practice.id, duration_min: Number(f.duration_min) || 60, reminder_hours: Number(f.reminder_hours) || 0,
        assigned_to: f.assigned_to === "none" ? null : f.assigned_to });
      toast.success(f.tipo === "appuntamento" && f.cellulare ? "Evento creato · il cliente riceverà la conferma WhatsApp tra 5 minuti" : "Evento creato");
      onCreated(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="practice-event-dialog">
        <DialogHeader><DialogTitle>Nuovo evento della pratica</DialogTitle><DialogDescription>L'evento sarà collegato alla pratica {practice.numero || ""} e visibile in calendario.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Titolo *</Label><Input value={f.titolo} onChange={set("titolo")} data-testid="pe-titolo" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={f.tipo} onValueChange={set("tipo")}><SelectTrigger data-testid="pe-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Data e ora *</Label><Input type="datetime-local" value={f.start} onChange={set("start")} data-testid="pe-start" /></div>
            <div className="space-y-1.5"><Label>Durata (min)</Label><Input type="number" min="5" step="5" value={f.duration_min} onChange={set("duration_min")} data-testid="pe-durata" /></div>
            <div className="space-y-1.5"><Label>Promemoria WhatsApp (ore prima)</Label><Input type="number" min="0" value={f.reminder_hours} onChange={set("reminder_hours")} placeholder="0 = 24 ore prima" data-testid="pe-reminder" /></div>
            <div className="space-y-1.5"><Label>Sede *</Label>
              <Select value={f.sede_id} onValueChange={set("sede_id")}><SelectTrigger data-testid="pe-sede"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                <SelectContent>{sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Collaboratore</Label>
              <Select value={f.assigned_to} onValueChange={set("assigned_to")}><SelectTrigger data-testid="pe-assigned"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Nome cliente</Label><Input value={f.cliente_nome} onChange={set("cliente_nome")} data-testid="pe-cliente" /></div>
            <div className="space-y-1.5"><Label>Cellulare</Label><Input value={f.cellulare} onChange={set("cellulare")} data-testid="pe-cellulare" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={set("email")} data-testid="pe-email" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.note} onChange={set("note")} rows={2} data-testid="pe-note" /></div>
          {f.tipo === "appuntamento" && <p className="text-xs text-muted-foreground">Per gli appuntamenti il cliente riceve il WhatsApp di conferma 5 minuti dopo il salvataggio e il promemoria prima dell'appuntamento.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="pe-save-btn">{busy ? "Salvataggio…" : "Crea evento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
